import { supabase } from "@/lib/supabase";
import type { PostgrestError } from "@supabase/supabase-js";
import type { Discount, Restaurant, RestaurantEvent, RestaurantId } from "@/types/restaurant";
import { advanceMissingColumnFlags, restaurantColumns } from "./columns";
import { fetchAllPages } from "./paging";

export async function listRestaurants(
  city?: string,
  opts?: { visibleOnly?: boolean },
): Promise<Restaurant[]> {
  const run = (columns: string) =>
    fetchAllPages((from, to) => {
      let query = supabase.from("restaurants").select(columns);
      if (city) query = query.eq("city", city);
      // Consumer feed asks for visibleOnly so hidden venues (now the bulk of the
      // catalog) never ship to the browser — smaller payload + their data isn't
      // exposed. Operator / POS / floor-bus omit it and still get every venue.
      if (opts?.visibleOnly) query = query.eq("visible", true);
      // `name` is the display order; `id` is the unique tiebreaker that keeps
      // pagination windows stable (venue names aren't unique — chains repeat).
      return query.order("name", { ascending: true }).order("id", { ascending: true }).range(from, to);
    });
  let data: unknown[];
  for (;;) {
    try {
      data = await run(restaurantColumns());
      break;
    } catch (error) {
      if (!advanceMissingColumnFlags(error as PostgrestError)) throw error;
    }
  }
  // Cast via unknown: a runtime (non-literal) column string makes supabase-js
  // infer GenericStringError[] rather than the row shape.
  return data as unknown as Restaurant[];
}

/**
 * Visible-venue counts keyed by city slug. Drives the Welcome gate: a city is
 * only selectable once an operator has added a restaurant there, and the count
 * renders next to the city name in the picker.
 */
export async function listCityVenueCounts(): Promise<Record<string, number>> {
  // Fast path: the §22a grouped-count RPC — one tiny round trip, immune to the
  // 1000-row cap. Falls back to a paginated client tally if the RPC isn't
  // deployed yet (pre-§22) or errors, so the public Welcome gate never breaks.
  const { data: rpcData, error: rpcError } = await supabase.rpc("city_venue_counts");
  if (!rpcError && Array.isArray(rpcData)) {
    const counts: Record<string, number> = {};
    for (const row of rpcData as Array<{ city: string | null; venue_count: number }>) {
      if (row.city) counts[row.city] = Number(row.venue_count);
    }
    return counts;
  }

  const data = (await fetchAllPages((from, to) =>
    supabase.from("restaurants").select("city, visible").order("id", { ascending: true }).range(from, to),
  )) as Array<{ city: string | null; visible?: boolean }>;
  const counts: Record<string, number> = {};
  // A city only counts if it has at least one *visible* venue, so cities whose
  // venues are all hidden fall back to the "Soon" state on the Welcome gate.
  // `visible` may be undefined on pre-migration rows — treat that as visible.
  for (const row of data) {
    if (row.city && row.visible !== false) counts[row.city] = (counts[row.city] ?? 0) + 1;
  }
  return counts;
}

export async function getRestaurant(id: RestaurantId): Promise<Restaurant | null> {
  const run = (columns: string) =>
    supabase.from("restaurants").select(columns).eq("id", id).maybeSingle();
  let { data, error } = await run(restaurantColumns());
  while (error && advanceMissingColumnFlags(error)) {
    ({ data, error } = await run(restaurantColumns()));
  }
  if (error) throw error;
  return (data as unknown as Restaurant | null) ?? null;
}

/**
 * A venue's login email via the staff-gated `get_venue_email` RPC (§19a) —
 * platform users may read any venue's, a manager only their own. The column
 * itself is unreadable through the REST grant for every browser role.
 */
export async function getVenueEmail(id: RestaurantId): Promise<string | null> {
  const { data, error } = await supabase.rpc("get_venue_email", { p_restaurant_id: id });
  if (error) throw error;
  return (data as string | null) ?? null;
}

// ---------------------------------------------------------------------------
// Events  (always tenant-scoped — pass restaurant_id, or null for "all")
// ---------------------------------------------------------------------------

export interface ListEventsOptions {
  /** Restrict to a single tenant. Omit for the global ticker feed. */
  restaurantId?: RestaurantId;
  /** Drop events whose `expires_at` is in the past. Default true. */
  activeOnly?: boolean;
}

export async function listEvents(
  options: ListEventsOptions = {},
): Promise<RestaurantEvent[]> {
  const data = await fetchAllPages((from, to) => {
    let query = supabase.from("events").select("*");
    if (options.restaurantId) query = query.eq("restaurant_id", options.restaurantId);
    if (options.activeOnly !== false) {
      // Match either "no expiry" or "expires in the future".
      query = query.or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`);
    }
    return query.order("id", { ascending: true }).range(from, to);
  });
  return data as RestaurantEvent[];
}

// ---------------------------------------------------------------------------
// Discounts
// ---------------------------------------------------------------------------

/**
 * Every restaurant's active discounts, grouped by restaurant_id and ordered
 * oldest-first (so the consumer card evaluates them in the same order the
 * operator sees them). One query feeds the whole feed — no N+1 loop. The card
 * picks whichever entry's time-window currently contains "now".
 */
export async function listActiveDiscountsByRestaurant(): Promise<
  Record<RestaurantId, Discount[]>
> {
  const data = (await fetchAllPages((from, to) =>
    supabase
      .from("discounts")
      .select("*")
      .eq("active", true)
      // created_at is the display order; id is the unique pagination tiebreaker.
      .order("created_at", { ascending: true })
      .order("id", { ascending: true })
      .range(from, to),
  )) as Discount[];

  const out: Record<RestaurantId, Discount[]> = {};
  for (const d of data) {
    (out[d.restaurant_id] ??= []).push(d);
  }
  return out;
}

export async function listAllDiscounts(restaurantId: RestaurantId): Promise<Discount[]> {
  // Order by creation time so the operator's list keeps a stable 1..N ordinal
  // (the UI numbers rows by position; insertion order makes "#1" the oldest).
  const { data, error } = await supabase
    .from("discounts")
    .select("*")
    .eq("restaurant_id", restaurantId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as Discount[];
}

/**
 * Every discount across every restaurant, oldest-first. Feeds the operator's
 * global "Special Events" browser. Includes inactive ones so the operator can
 * see and edit everything they've authored. Paged: a bare select would stop
 * silently at PostgREST's 1000-row cap once the catalog's offers outgrow it.
 */
export async function listAllDiscountsGlobal(): Promise<Discount[]> {
  const data = await fetchAllPages((from, to) =>
    supabase
      .from("discounts")
      .select("*")
      // created_at is the display order; id is the unique pagination tiebreaker.
      .order("created_at", { ascending: true })
      .order("id", { ascending: true })
      .range(from, to),
  );
  return data as Discount[];
}

// ---------------------------------------------------------------------------
// Go-live one-time links
// ---------------------------------------------------------------------------
//
// A hidden venue (visible=false) has a single-use token in `golive_links`,
// minted by a DB trigger. Operators read it here to share the onboarding link;
// the public burns it via the `redeem_golive_link` RPC (see schema.sql §9).
// ---------------------------------------------------------------------------

/**
 * Look up the venue name for a go-live token WITHOUT consuming it. Returns null
 * if the token is invalid / already used. Used by the go-live page to render a
 * confirm screen, so link previews can't activate the venue early.
 */
export async function peekGoLiveLink(token: string): Promise<string | null> {
  const { data, error } = await supabase.rpc("peek_golive_link", { p_token: token });
  if (error) throw error;
  return (data as string | null) ?? null;
}

/**
 * Every go-live token, keyed by restaurant. Lets the operator pull all
 * onboarding links at once (RLS allows platform users to read this table).
 */
export async function listGoLiveLinks(): Promise<Record<RestaurantId, string>> {
  // `restaurant_id` is unique in golive_links (one row per venue) → a stable
  // total order for pagination. Without `.range()` this capped at 1000 tokens
  // and, unordered, misaligned with the venue list → phantom "(no link)".
  const data = (await fetchAllPages((from, to) =>
    supabase
      .from("golive_links")
      .select("restaurant_id, token")
      .order("restaurant_id", { ascending: true })
      .range(from, to),
  )) as Array<{ restaurant_id: string; token: string }>;
  const out: Record<RestaurantId, string> = {};
  for (const row of data) {
    out[row.restaurant_id] = row.token;
  }
  return out;
}

/** The current go-live token for a hidden venue, or null if it's visible. */
export async function getGoLiveToken(restaurantId: RestaurantId): Promise<string | null> {
  const { data, error } = await supabase
    .from("golive_links")
    .select("token")
    .eq("restaurant_id", restaurantId)
    .maybeSingle();
  if (error) throw error;
  return (data?.token as string | undefined) ?? null;
}

// ---------------------------------------------------------------------------
// Manager logins (admin "Manager logins" page)
// ---------------------------------------------------------------------------

/** One provisioned manager login, for the admin "Manager logins" page. */
export interface ManagerCredential {
  restaurant_id: RestaurantId;
  restaurant_name: string;
  city: string | null;
  visible: boolean | null;
  email: string;
  password: string;
  created_at: string;
}

/**
 * Every provisioned manager login, newest first, for the admin credentials
 * page. Joins the venue name/visibility. RLS (§12e) restricts the whole table
 * to platform users, so this returns nothing for anyone else.
 */
export async function listManagerCredentials(): Promise<ManagerCredential[]> {
  const data = await fetchAllPages((from, to) =>
    supabase
      .from("manager_credentials")
      .select("restaurant_id, email, password, created_at, restaurants(name, city, visible)")
      // created_at is the display order; restaurant_id (unique here) is the
      // stable pagination tiebreaker.
      .order("created_at", { ascending: false })
      .order("restaurant_id", { ascending: true })
      .range(from, to),
  );
  type Row = {
    restaurant_id: string;
    email: string;
    password: string;
    created_at: string;
    restaurants: { name: string; city: string | null; visible: boolean | null } | null;
  };
  return (data as unknown as Row[]).map((r) => ({
    restaurant_id: r.restaurant_id,
    restaurant_name: r.restaurants?.name ?? "(unknown)",
    city: r.restaurants?.city ?? null,
    visible: r.restaurants?.visible ?? null,
    email: r.email,
    password: r.password,
    created_at: r.created_at,
  }));
}
